<template>
    <div class="cbi-container">
        <!-- Header Section -->
        <div class="page-header">
            <div class="header-content">
                <div class="header-left">
                    <div class="header-icon">
                        <b-icon icon="hdd-stack-fill" font-scale="2"></b-icon>
                    </div>
                    <div class="header-text">
                        <h1 class="page-title">Gestión de CBI</h1>
                        <p class="page-subtitle">Administra los equipos CBI del sistema</p>
                    </div>
                </div>
                <div class="header-right" v-if="tipoUnidades === 'enlazadas' || tipoUnidades === 'no_enlazadas'">
                    <div class="stats-cards">
                        <div class="stat-card stat-on">
                            <div class="stat-icon">
                                <b-icon icon="power"></b-icon>
                            </div>
                            <div class="stat-content">
                                <span class="stat-number">{{ estadosEquipos.ON || 0 }}</span>
                                <span class="stat-label">ON</span>
                            </div>
                        </div>
                        <div class="stat-card stat-off">
                            <div class="stat-icon">
                                <b-icon icon="stop-circle-fill"></b-icon>
                            </div>
                            <div class="stat-content">
                                <span class="stat-number">{{ estadosEquipos.OFF || 0 }}</span>
                                <span class="stat-label">OFF</span>
                            </div>
                        </div>
                        <div class="stat-card stat-disconnected" v-if="tipoUnidades === 'enlazadas'">
                            <div class="stat-icon">
                                <b-icon icon="wifi-off"></b-icon>
                            </div>
                            <div class="stat-content">
                                <span class="stat-number">{{ estadosEquipos.Desconectado || 0 }}</span>
                                <span class="stat-label">Desconectado</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="header-right" v-else>
                    <div class="stats-cards">
                        <div class="stat-card stat-disconnected" >
                            <div class="stat-icon">
                                <b-icon icon="wifi-off"></b-icon>
                            </div>
                            <div class="stat-content">
                                <span class="stat-number">{{ totalAlertas || 0 }}</span>
                                <span class="stat-label">Total alertas</span>
                            </div>
                        </div>
                        <div class="stat-card stat-off">
                            <div class="stat-icon">
                                <b-icon icon="stop-circle-fill"></b-icon>
                            </div>
                            <div class="stat-content">
                                <span class="stat-number">{{ totalUnidadesAlertadas || 0 }}</span>
                                <span class="stat-label">Unidades alertadas</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Filters Section -->
        <div class="filters-card">
            <div class="filters-content">
                <div class="filter-group">
                    <label class="filter-label">Tipo de Unidades</label>
                    <button class="btn-filter-type" @click="openModal">
                        <b-icon icon="funnel-fill" class="mr-1"></b-icon>
                        {{ 
                        tipoUnidades === 'enlazadas' 
                            ? 'CBI Enlazados' 
                            : (tipoUnidades === 'con_alertas') 
                                ? 'Unidades con alertas'
                                : (tipoUnidades === 'requiren_mantenimiento')
                                    ? 'Unidades que requieren mantenimiento'
                                : 'CBI No Enlazados' }}
                        <b-icon icon="chevron-down" class="ml-1"></b-icon>
                    </button>
                </div>
                <div class="filter-group">
                    <label class="filter-label">Buscar CBI</label>
                    <input
                        type="text"
                        class="filter-input"
                        v-model="searchTerm"
                        @keyup.enter="buscarCBI"
                        placeholder="Ingrese código CBI y presione Enter o haga clic en buscar..."
                        :disabled="buscandoCBI"
                    />
                </div>
                <div class="filter-group">
                    <label class="filter-label">Sede</label>
                    <select
                        class="filter-select"
                        v-model="filterSede"
                        @change="filterCBIsLocal"
                    >
                        <option value="">Todas las sedes</option>
                        <option v-for="sede in sedes" :key="sede.id" :value="sede.id">
                            {{ sede.nombre }}
                        </option>
                    </select>
                </div>
                <div class="filter-group">
                    <div class="action-buttons-filter">
                        <button 
                            class="btn-search" 
                            @click="buscarCBI"
                            :disabled="!searchTerm.trim() || buscandoCBI"
                            title="Buscar CBI en la API">
                            <b-icon v-if="!buscandoCBI" icon="search"></b-icon>
                            <b-spinner v-else small></b-spinner>
                        </button>
                        <button 
                            class="btn-clear" 
                            @click="clearFilters"
                            title="Limpiar filtros">
                            <b-icon icon="arrow-clockwise"></b-icon>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="content-card">
            <!-- Loading State -->
            <div v-if="loading" class="loading-container">
                <div class="loading-content">
                    <b-spinner variant="primary" class="loading-spinner"></b-spinner>
                    <p class="loading-text">Cargando unidades...</p>
                    <small class="text-muted">Esto puede tomar unos segundos...</small>
                </div>
            </div>

            <!-- CBIs Table -->
            <div v-else class="table-container">
                <!-- Mensaje informativo para agrupación -->
                <div v-if="tipoUnidades === 'con_alertas' || tipoUnidades === 'requiren_mantenimiento'" 
                     class="info-banner">
                    <div class="info-content">
                        <b-icon icon="info-circle-fill" class="info-icon"></b-icon>
                        <div class="info-text">
                            <strong>Registros agrupados por CBI:</strong> 
                            Los registros duplicados por CBI se muestran como una sola fila con todos los códigos de alerta combinados.
                        </div>
                    </div>
                </div>
                
                <div class="custom-table">
                    <div class="table-header">
                        <div class="table-row header-row">
                            <div class="table-cell">#</div>
                            <div class="table-cell">Ubicación Completa</div>
                            <div class="table-cell">CBI</div>
                            <div class="table-cell">Estado</div>
                            <div class="table-cell" v-if="tipoUnidades === 'enlazadas' || tipoUnidades === 'no_enlazadas'">Acciones</div>
                            <div class="table-cell" v-else>Cod Alerta</div>
                        </div>
                    </div>
                    <div class="table-body">
                        <div v-for="(unidad, index) in paginatedCBIs" 
                             :key="index" 
                             class="table-row data-row">
                            <div class="table-cell">
                                {{ index + 1 + (paginaActual - 1) * itemsPorPagina }}
                            </div>
                            <div class="table-cell" v-if="tipoUnidades !== 'no_enlazadas'">
                                <!-- <router-link 
                                    :to="(tipoUnidades === 'enlazadas')?`/ver-unidad/${unidad.id}`:''" 
                                    class="unit-link"
                                >
                                    {{ (unidad.ubicacion_completa || '').toLowerCase() }}
                                </router-link> -->
                                <span @click="validarLectura(unidad.cbi, unidad.id)" class="unit-link" type="button">
                                    {{ (unidad.ubicacion_completa || '').toLowerCase() }}
                                </span>
                            </div>
                            
                            
                            <div class="table-cell">
                                <div class="cbi-info">
                                    <div class="cbi-avatar">
                                        <b-icon icon="hdd-fill" font-scale="1.5"></b-icon>
                                    </div>
                                    <span class="cbi-code">{{ unidad.cbi || '-' }}</span>
                                </div>
                            </div>
                            
                            <div class="table-cell">
                                <span :class="['status-badge', getStatusClass(unidad.estado_equipo || unidad.tipo_registro)]">
                                    {{ getStatusText(unidad.estado_equipo || unidad.tipo_registro) }}
                                </span>
                            </div>
                            <div class="table-cell" v-if="tipoUnidades === 'enlazadas' || tipoUnidades === 'no_enlazadas'">
                                <div class="action-buttons" v-if="tipoUnidades === 'no_enlazadas'">
                                    <button class="btn-link" 
                                            @click="enlazarUnidad(unidad)" 
                                            :disabled="submitting">
                                        <b-icon icon="link-45deg" class="mr-1"></b-icon>
                                        Enlazar
                                    </button>
                                    <button class="btn-link btn-danger ml-2" 
                                            @click="eliminarLecturasCbi(unidad.cbi)" 
                                            :disabled="submitting"
                                            title="Eliminar lecturas de este CBI">
                                        <b-icon icon="trash" class="mr-1"></b-icon>
                                        Eliminar
                                    </button>
                                </div>
                                <div class="action-buttons" v-else>
                                    <span class="text-muted">-</span>
                                </div>
                            </div>
                            <div class="table-cell" v-else>
                                <div v-if="unidad.cod_alerta" class="alert-badges-container">
                                    <!-- Si hay múltiples códigos separados por coma -->
                                    <div v-if="unidad.cod_alerta.includes(',')">
                                        <div v-for="(codigo, codIndex) in unidad.cod_alerta.split(', ')" 
                                             :key="`alert-group-${index}-${codIndex}`"
                                             class="alert-badge-wrapper">
                                            <b-badge 
                                                :id="`alerta-badge-${index}-${codIndex}`"
                                                :variant="getAlertVariant(codigo.trim())"
                                                class="alert-badge multiple-alerts"
                                            >
                                                {{ codigo.trim() }}
                                            </b-badge>
                                            <!-- Tooltip individual para cada badge -->
                                            <b-tooltip 
                                                :target="`alerta-badge-${index}-${codIndex}`" 
                                                placement="top"
                                                class="alert-tooltip"
                                            >
                                                {{ getDetalleAlertaEspecifico(unidad, codigo.trim()) }}
                                            </b-tooltip>
                                        </div>
                                    </div>
                                    <!-- Si hay un solo código -->
                                    <div v-else>
                                        <b-badge 
                                            :id="`alerta-badge-${index}`"
                                            :variant="getAlertVariant(unidad.cod_alerta)"
                                            class="alert-badge"
                                        >
                                            {{ unidad.cod_alerta }}
                                        </b-badge>
                                        <!-- Tooltip para badge único -->
                                        <b-tooltip 
                                            :target="`alerta-badge-${index}`" 
                                            placement="top"
                                            class="alert-tooltip"
                                        >
                                            {{ getDetalleAlertaEspecifico(unidad, unidad.cod_alerta) }}
                                        </b-tooltip>
                                    </div>
                                </div>
                                <span v-else class="text-muted">N/A</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div class="pagination-container" v-if="filteredCBIs.length > 0">
                    <div class="pagination-info">
                        <p class="text-muted">
                            Mostrando {{ ((paginaActual - 1) * itemsPorPagina) + 1 }} - 
                            {{ Math.min(paginaActual * itemsPorPagina, totalRegistros) }} 
                            de {{ totalRegistros }} registro{{ totalRegistros !== 1 ? 's' : '' }}
                            - Página {{ paginaActual }} de {{ totalPaginas }}
                        </p>
                    </div>
                    <b-pagination
                        v-model="paginaActual"
                        :total-rows="totalRegistros"
                        :per-page="itemsPorPagina"
                        class="custom-pagination"
                        size="sm"
                        align="center"
                    ></b-pagination>
                </div>
            </div>
        </div>

        <!-- Modal para seleccionar tipo de unidades -->
        <b-modal v-model="showModal" 
                 title="Seleccionar Tipo de Unidades" 
                 size="md"
                 centered
                 hide-footer>
            <div class="modal-options">
                <div class="option-card" 
                     :class="{ active: tipoUnidades === 'enlazadas' }"
                     @click="selectTipoUnidades('enlazadas')">
                    <div class="option-icon">
                        <b-icon icon="link-45deg" font-scale="2"></b-icon>
                    </div>
                    <div class="option-content">
                        <h5 class="option-title">CBI Enlazados</h5>
                        <p class="option-description">CBI enlazados a unidades dentro del sistema</p>
                    </div>
                </div>
                
                <div class="option-card" 
                     :class="{ active: tipoUnidades === 'no_enlazadas' }"
                     @click="selectTipoUnidades('no_enlazadas')">
                    <div class="option-icon">
                        <b-icon icon="unlock" font-scale="2"></b-icon>
                    </div>
                    <div class="option-content">
                        <h5 class="option-title">CBI No Enlazados</h5>
                        <p class="option-description">CBI no enlazados a unidades dentro del sistema</p>
                    </div>
                </div>
                <div class="option-card" 
                     :class="{ active: tipoUnidades === 'con_alertas' }"
                     @click="selectTipoUnidades('con_alertas')">
                    <div class="option-icon">
                        <b-icon icon="exclamation-triangle-fill" font-scale="2"></b-icon>
                    </div>
                    <div class="option-content">
                        <h5 class="option-title">Unidades con alertas</h5>
                        <p class="option-description">Unidades con alertas en el historial de operaciones</p>
                    </div>
                </div>
                <div class="option-card" 
                     :class="{ active: tipoUnidades === 'requiren_mantenimiento' }"
                     @click="selectTipoUnidades('requiren_mantenimiento')">
                    <div class="option-icon">
                        <b-icon icon="clock" font-scale="2"></b-icon>
                    </div>
                    <div class="option-content">
                        <h5 class="option-title">Unidades que requieren mantenimiento</h5>
                        <p class="option-description">Unidades que requieren mantenimiento preventivo o correctivo</p>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- Loader Modal para validación de lectura -->
        <div v-if="validandoLectura" class="validation-loader-overlay">
            <div class="validation-loader-content">
                <div class="loader-spinner-container">
                    <b-spinner variant="light" class="loader-spinner"></b-spinner>
                </div>
                <div class="loader-text">
                    <h4>Validando lectura del CBI</h4>
                    <p>Consultando si el CBI tiene lecturas en el sistema...</p>
                    <small class="text-muted">Por favor, espere un momento</small>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'GestionCBI',
    props: {
        // Definir props si es necesario
        filtroUnidad: {
            type: String,
            default: 'enlazadas'
        }
    },
    data() {
        return {
            // Datos principales
            cbis: [],
            filteredCBIs: [],
            sedes: [],
            loading: false,
            submitting: false,
            validandoLectura: false, // Loader para validación de lectura
            
            // Estadísticas
            totalUnidadesCreadas: 0,
            totalUnidadesNoCreadas: 0,
            estadosEquipos: {
                ON: 0,
                OFF: 0,
                Desconectado: 0,
                'Sin datos': 0
            },
            
            // Filtros
            searchTerm: '',
            filterStatus: '',
            filterSede: '',
            tipoUnidades: this.filtroUnidad,
            showModal: false,
            buscandoCBI: false,
            resultadoBusquedaCBI: null,
            
            // Paginación real con API
            paginaActual: 1,
            itemsPorPagina: 15, 
            totalAlertas: 0,
            totalUnidadesAlertadas: 0,
            totalPaginas: 1,
            totalRegistros: 0,
            
            // Control de estado
            usingFallbackData: false

        };
    },
    
    computed: {
        totalCBIs() {
            return this.totalRegistros;
        },
        
        paginatedCBIs() {
            // Con paginación real, los datos ya vienen filtrados de la API
            return this.filteredCBIs;
        }
    },
    
    watch: {
        paginaActual: function(newPage, oldPage) {
            if (newPage !== oldPage) {
                this.loadCBIs();
            }
        },
        
        tipoUnidades: function(newType, oldType) {
            if (newType !== oldType && !this.loading) {
                this.paginaActual = 1;
                this.loadCBIs();
            }
        }
    },
    
    mounted() {
        document.title = 'Gestión de CBI';
        this.loadData();
    },
    
    methods: {
        validarLectura(cbi, unidadId){
            this.validandoLectura = true;
            
            axios.get(`/api/validar-lectura-cbi/${cbi}`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.user.token}`
                }
            })
            .then(response => {
                const lecturaValida = response.data.lectura;
                if(lecturaValida){
                    this.$router.push(`/ver-unidad/${unidadId}`);
                } else {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Lectura Inválida',
                        text: 'No se ha recibido una lectura válida de este CBI. Por favor, verifique la conexión del equipo.',
                        timer: 4000,
                        showConfirmButton: false
                    });
                }
            })
            .catch(error => {
                console.error('Error validando lectura del CBI:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error de Validación',
                    text: 'Ocurrió un error al validar la lectura del CBI. Intente nuevamente más tarde.',
                    timer: 4000,
                    showConfirmButton: false
                });
            })
            .finally(() => {
                this.validandoLectura = false;
            });
        },
        async loadData() {
            this.loading = true;
            
            try {
                // Cargar solo unidades enlazadas por defecto
                await this.loadCBIs();
                
                // Cargar sedes después
                await this.loadSedes();
                
            } catch (error) {
                console.error('Error loading data:', error);
                
                // Mostrar mensaje de advertencia al usuario
                if (window.Swal) {
                    window.Swal.fire({
                        icon: 'warning',
                        title: 'Problema de Conexión',
                        text: 'No se pudo conectar con el servidor. Mostrando datos de ejemplo.',
                        timer: 3000,
                        showConfirmButton: false
                    });
                }
                
                // Fallback con datos de ejemplo
                this.usingFallbackData = true;
                this.loadSampleData();
                this.sedes = [
                    { id: 1, nombre: 'Sede Administrativa' },
                    { id: 2, nombre: 'Hospital San Jerónimo' }
                ];
            } finally {
                this.loading = false;
            }
        },
        
        // ✅ Método para agrupar unidades por CBI y combinar códigos de alerta
        agruparUnidadesPorCBI(unidades) {
            const unidadesAgrupadas = {};
            
            // Agrupar por CBI
            unidades.forEach(unidad => {
                const cbi = unidad.cbi;
                
                if (!unidadesAgrupadas[cbi]) {
                    // Primera vez que vemos este CBI, crear entrada base
                    unidadesAgrupadas[cbi] = {
                        ...unidad,
                        alertas_map: new Map() // Usar Map para mantener correspondencia código-detalle
                    };
                }
                
                // Añadir par código-detalle si existe
                if (unidad.cod_alerta) {
                    const codigo = unidad.cod_alerta.trim();
                    const detalle = unidad.detalle_alerta ? unidad.detalle_alerta.trim() : '';
                    
                    // Solo añadir si no existe este código
                    if (!unidadesAgrupadas[cbi].alertas_map.has(codigo)) {
                        unidadesAgrupadas[cbi].alertas_map.set(codigo, detalle);
                    }
                }
            });
            
            // Convertir objeto a array y procesar códigos de alerta
            return Object.values(unidadesAgrupadas).map(unidad => {
                if (unidad.alertas_map && unidad.alertas_map.size > 0) {
                    // Convertir Map a arrays ordenados por prioridad
                    const alertasArray = Array.from(unidad.alertas_map.entries());
                    
                    // Ordenar por prioridad (críticos primero)
                    alertasArray.sort((a, b) => {
                        const prioridadA = this.getPrioridadAlerta(a[0]);
                        const prioridadB = this.getPrioridadAlerta(b[0]);
                        return prioridadB - prioridadA; // Orden descendente
                    });
                    
                    // Extraer códigos y crear string combinado
                    const codigosOrdenados = alertasArray.map(([codigo]) => codigo);
                    unidad.cod_alerta = codigosOrdenados.join(', ');
                    
                    // Crear objeto de mapeo para tooltips específicos
                    unidad.alertas_detalle_map = {};
                    alertasArray.forEach(([codigo, detalle]) => {
                        unidad.alertas_detalle_map[codigo] = detalle;
                    });
                    
                    // Mantener detalle_alerta como fallback
                    unidad.detalle_alerta = alertasArray.map(([, detalle]) => detalle).join(' | ');
                } else {
                    unidad.cod_alerta = null;
                    unidad.detalle_alerta = null;
                    unidad.alertas_detalle_map = {};
                }
                
                // Limpiar propiedades temporales
                delete unidad.alertas_map;
                
                return unidad;
            });
        },
        
        async loadCBIs() {
            try {
                let endpoint = '';

                if(this.tipoUnidades === 'con_alertas'){
                    endpoint = '/api/index-unidades-con-alertas';
                } else if(this.tipoUnidades === 'requiren_mantenimiento'){
                    endpoint = '/api/index-unidades-requieren-mantenimiento';
                }else if(this.tipoUnidades === 'enlazadas'){
                    endpoint = '/api/unidades-creadas';
                } else {
                    endpoint = '/api/unidades-no-creadas';
                }   

                // ✅ Para alertas y mantenimiento, cargar más registros para compensar la agrupación
                const perPage = (this.tipoUnidades === 'con_alertas' || this.tipoUnidades === 'requiren_mantenimiento') 
                    ? this.itemsPorPagina * 3  // Cargar 3x más registros para agrupar
                    : this.itemsPorPagina;

                const response = await axios.get(endpoint, {
                    headers: {
                        Authorization: `Bearer ${this.$store.state.user.token}`
                    },
                    params: {
                        page: this.paginaActual,
                        per_page: perPage
                    }
                });
            
                // Extraer datos de la paginación
                const paginationData = response.data.unidades;
                
                let unidadesData = paginationData.data || paginationData || [];
                
                // ✅ Para alertas y mantenimiento, agrupar por CBI y combinar códigos de alerta
                if (this.tipoUnidades === 'con_alertas' || this.tipoUnidades === 'requiren_mantenimiento') {
                    unidadesData = this.agruparUnidadesPorCBI(unidadesData);
                }
                
                this.cbis = unidadesData;
                this.totalPaginas = paginationData.last_page || 1;
                
                // ✅ Para alertas y mantenimiento, el total de registros es el número de CBIs únicos
                if (this.tipoUnidades === 'con_alertas' || this.tipoUnidades === 'requiren_mantenimiento') {
                    this.totalRegistros = unidadesData.length;
                } else {
                    this.totalRegistros = paginationData.total || 0;
                }
                
                // Estadísticas según el tipo de endpoint (solo del tipo actual)
                const stats = response.data.estadisticas;
                
                if(this.tipoUnidades === 'con_alertas'){
                    this.totalAlertas = stats?.total_alertas_activas || 0;
                    this.totalUnidadesAlertadas = stats?.total_unidades_con_alertas || 0;
                } else if(this.tipoUnidades === 'requiren_mantenimiento'){
                    this.totalUnidadesAlertadas = stats?.total_unidades_requieren_mantenimiento || 0;
                    this.totalAlertas = stats?.total_alertas_mantenimiento || 0;
                } else if (this.tipoUnidades === 'enlazadas') {
                    this.totalUnidadesCreadas = stats?.total_unidades_creadas || 0;
                    this.estadosEquipos = stats?.estados || {
                        ON: 0,
                        OFF: 0,
                        Desconectado: 0,
                        'Sin datos': 0
                    };
                } else {
                    this.totalUnidadesNoCreadas = 0;
                    this.estadosEquipos = {
                        ON: 0,
                        OFF: 0,
                        Desconectado: 0, // Agregar valor por defecto
                        'Sin datos': 0   // Agregar valor por defecto
                    };
                }
                
                // Para la paginación real, no necesitamos filtros locales
                this.filteredCBIs = [...this.cbis];
                
            } catch (error) {
                console.error('Error cargando CBIs:', error);
                
                // Manejo específico para timeouts
                if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                    Swal.fire({
                        title: 'Timeout de consulta',
                        text: 'La consulta está tardando más de lo esperado. Esto puede deberse al gran volumen de datos. ¿Desea intentar nuevamente?',
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: 'Reintentar',
                        cancelButtonText: 'Usar datos de ejemplo'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            this.loadCBIs(); // Reintentar
                        } else {
                            this.usingFallbackData = true;
                            this.loadSampleData();
                        }
                    });
                } else {
                    // En caso de otros errores, usar datos de muestra
                    this.usingFallbackData = true;
                    this.loadSampleData();
                }
            } finally {
                this.loading = false; // Ocultar loader siempre
            }
        },
        

        
        loadSampleData() {
            this.cbis = [
                {
                    id: 1,
                    sede: 'Sede Administrativa',
                    cbi: 'CBI001',
                    ubicacion_completa: 'Bloque A - Piso 1 - Oficina 101',
                    estado_equipo: 'ON',
                    tipo_registro: 'creada'
                },
                {
                    id: 2,
                    sede: 'Hospital San Jerónimo',
                    cbi: 'CBI002',
                    ubicacion_completa: 'Bloque B - Piso 2 - Sala de máquinas',
                    estado_equipo: 'OFF',
                    tipo_registro: 'creada'
                }
            ];
            
            // Estadísticas de muestra
            this.totalUnidadesCreadas = 19; // Del modal en la imagen
            this.totalUnidadesNoCreadas = 0; // Del modal en la imagen
            this.estadosEquipos = {
                ON: 0,
                OFF: 10,
                Desconectado: 9,
                'Sin datos': 0
            };
            
            this.totalPaginas = 1;
            this.totalRegistros = 2;
            this.filteredCBIs = [...this.cbis];
        },
        
        async loadSedes() {
            try {
                const response = await axios.get('/api/sedes', {
                    headers: {
                        Authorization: `Bearer ${this.$store.state.user.token}`
                    },
                    timeout: 5000 // 5 segundos
                });
                this.sedes = response.data.sedes || response.data || [];
            } catch (error) {
                console.error('Error cargando sedes:', error);
                this.sedes = [
                    { id: 1, nombre: 'Sede Administrativa' },
                    { id: 2, nombre: 'Hospital San Jerónimo' }
                ];
            }
        },
        
        async buscarCBI() {
            if (!this.searchTerm.trim()) {
                Swal.fire({
                    title: 'Atención',
                    text: 'Ingrese un código CBI para buscar',
                    icon: 'warning',
                    timer: 2000,
                    showConfirmButton: false
                });
                return;
            }

            this.buscandoCBI = true;
            this.resultadoBusquedaCBI = null;
            
            try {
                const response = await axios.get(`/api/unidades-creadas/${this.searchTerm.trim()}`, {
                    headers: {
                        Authorization: `Bearer ${this.$store.state.user.token}`
                    }
                });

                console.log('Respuesta de búsqueda CBI:', response.data);

                if (response.data.unidades && response.data.unidades.data.length > 0) {
                    this.resultadoBusquedaCBI = response.data;
                    this.cbis = response.data.unidades.data;
                    this.filteredCBIs = [...response.data.unidades.data];
                    this.totalRegistros = response.data.unidades.data.length;
                    this.paginaActual = 1;
                    
                    Swal.fire({
                        title: 'Búsqueda exitosa',
                        text: `Se encontraron ${response.data.length} resultado(s) para el CBI "${this.searchTerm}"`,
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false
                    });
                } else {
                    this.cbis = [];
                    this.filteredCBIs = [];
                    this.totalRegistros = 0;
                    
                    Swal.fire({
                        title: 'Sin resultados',
                        text: `No se encontraron unidades con el CBI "${this.searchTerm}"`,
                        icon: 'info',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            } catch (error) {
                console.error('Error buscando CBI:', error);
                
                if (error.response?.status === 404) {
                    Swal.fire({
                        title: 'CBI no encontrado',
                        text: `El CBI "${this.searchTerm}" no existe en el sistema`,
                        icon: 'warning',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    Swal.fire({
                        title: 'Error de búsqueda',
                        text: 'Ocurrió un error al buscar el CBI. Intente nuevamente.',
                        icon: 'error',
                        timer: 3000,
                        showConfirmButton: false
                    });
                }
                
                this.cbis = [];
                this.filteredCBIs = [];
                this.totalRegistros = 0;
            } finally {
                this.buscandoCBI = false;
            }
        },

        filterCBIsLocal() {
            let filtered = [...this.cbis];
            
            // Filtro por sede
            if (this.filterSede) {
                filtered = filtered.filter(unidad => {
                    if (!unidad.sede) return false;
                    
                    // Si sede es string, comparar directamente
                    if (typeof unidad.sede === 'string') {
                        // Buscar la sede por nombre en la lista de sedes
                        const sedeEncontrada = this.sedes.find(s => s.nombre === unidad.sede);
                        return sedeEncontrada && sedeEncontrada.id === this.filterSede;
                    }
                    
                    // Si sede es objeto, comparar el id
                    if (typeof unidad.sede === 'object' && unidad.sede.id) {
                        return unidad.sede.id === this.filterSede;
                    }
                    
                    return false;
                });
            }
            
            this.filteredCBIs = filtered;
            this.paginaActual = 1;
        },
        
        clearFilters() {
            this.searchTerm = '';
            this.filterStatus = '';
            this.filterSede = '';
            this.resultadoBusquedaCBI = null;
            
            // Recargar datos originales
            this.paginaActual = 1;
            this.loadCBIs();
        },
        
        openModal() {
            // Abrir modal inmediatamente sin cargar estadísticas adicionales
            this.showModal = true;
        },
        
        async selectTipoUnidades(tipo) {
            // Mostrar mensaje informativo para unidades no enlazadas
            if (tipo === 'no_enlazadas') {
                Swal.fire({
                    title: 'Cargando datos...',
                    text: 'Las unidades no enlazadas pueden tardar más tiempo en cargar debido al volumen de datos.',
                    icon: 'info',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
            
            this.tipoUnidades = tipo;
            this.showModal = false;
            this.paginaActual = 1;
            this.loading = true; // Mostrar loader
            await this.loadCBIs();
        },
        

        
        enlazarUnidad(unidad) {
            if (window.Swal) {
                window.Swal.fire({
                    title: 'Enlazar Unidad',
                    text: `¿Desea enlazar la unidad en ${unidad.ubicacion_completa}?`,
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: 'Sí, Enlazar',
                    cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#1a2732'
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.$router.push({name: 'CrearUnidad', params: {sede_id: 0, bloque_id: 0, cbi: unidad.cbi}})
                    }
                });
            } else {
                if (confirm(`¿Desea enlazar la unidad en ${unidad.ubicacion_completa}?`)) {
                    this.$router.push({name: 'CrearUnidad', params: {sede_id: 0, bloque_id: 0, cbi: unidad.cbi}})
                }
            }
        },

        async eliminarLecturasCbi(cbi) {
            if (this.submitting || !cbi) return;
            
            // Mostrar confirmación antes de eliminar
            const confirmResult = await window.Swal.fire({
                title: '¿Estás seguro?',
                text: `Se eliminarán todas las lecturas del CBI: ${cbi}. Esta acción no se puede deshacer.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });

            if (!confirmResult.isConfirmed) return;
            
            this.submitting = true;
            try {
                const response = await axios.delete(`/api/eliminar-lecturas-cbi/${cbi}`, {
                    headers: {
                        'Authorization': `Bearer ${this.$store.state.user?.token}`
                    }
                });

                if (response.data.status === 'success') {
                    window.Swal.fire({
                        title: 'Éxito',
                        html: `<div class="text-left">
                                <strong>${response.data.message}</strong><br><br>
                                <strong>CBI:</strong> ${response.data.cbi}<br>
                                <strong>Lecturas eliminadas:</strong> ${response.data.lecturas_eliminadas}<br>
                                <strong>Rango eliminado:</strong><br>
                                &nbsp;&nbsp;• Primera: ${response.data.rango_eliminado.primera_lectura}<br>
                                &nbsp;&nbsp;• Última: ${response.data.rango_eliminado.ultima_lectura}
                               </div>`,
                        icon: 'success',
                        confirmButtonText: 'OK'
                    });
                    
                    // Recargar los datos para reflejar los cambios
                    await this.loadData();
                } else {
                    throw new Error(response.data.message || 'Error al eliminar las lecturas');
                }
            } catch (error) {
                console.error('Error al eliminar lecturas CBI:', error);
                window.Swal.fire({
                    title: 'Error',
                    text: error.response?.data?.message || error.message || 'Error al eliminar las lecturas del CBI',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            } finally {
                this.submitting = false;
            }
        },
        
        async procesarEnlace(unidad) {
            try {
                this.submitting = true;
                
                const response = await axios.post(`/api/unidades/${unidad.id}/enlazar`, {}, {
                    headers: {
                        Authorization: `Bearer ${this.$store.state.user.token}`
                    }
                });
                
                // En lugar de recargar todo, solo actualizar el registro específico
                const index = this.cbis.findIndex(u => u.id === unidad.id);
                if (index !== -1) {
                    // Actualizar el registro local
                    this.cbis[index].tipo_registro = 'creada';
                    // Actualizar estadísticas
                    this.totalUnidadesCreadas++;
                    this.totalUnidadesNoCreadas--;
                    // Actualizar filtros
                    this.filteredCBIs = [...this.cbis];
                }
                
                if (window.Swal) {
                    window.Swal.fire({
                        icon: 'success',
                        title: 'Éxito',
                        text: 'Unidad enlazada correctamente',
                        timer: 1400,
                        showConfirmButton: false
                    });
                }
                
            } catch (error) {
                console.error('Error enlazando unidad:', error);
                if (window.Swal) {
                    window.Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'No se pudo enlazar la unidad'
                    });
                }
            } finally {
                this.submitting = false;
            }
        },

        
        getStatusClass(estado) {
            const classes = {
                'ON': 'status-on',
                'OFF': 'status-off',
                'Desconectado': 'status-disconnected',
                'Sin datos': 'status-no-data',
                'creada': 'status-active',
                'no_creada': 'status-inactive'
            };
            return classes[estado] || 'status-inactive';
        },
        
        getStatusText(estado) {
            const texts = {
                'ON': 'ON',
                'OFF': 'OFF',
                'Desconectado': 'Desconectado',
                'Sin datos': 'Sin datos',
                'creada': 'Creada',
                'no_creada': 'No Creada'
            };
            return texts[estado] || estado;
        },
        
        // ✅ Método para obtener la prioridad numérica de una alerta (mayor número = mayor prioridad)
        getPrioridadAlerta(codAlerta) {
            if (!codAlerta) return 0;
            
            const alertCode = codAlerta.toString().trim().toLowerCase();
            
            // Críticas (prioridad más alta)
            if (alertCode.includes('a007') || alertCode.includes('a005') || alertCode.includes('alto') || alertCode.includes('error')) {
                return 3;
            }
            // Advertencias (prioridad media)
            if (alertCode.includes('a006') || alertCode.includes('warning') || alertCode.includes('alerta')) {
                return 2;
            }
            // Informativas (prioridad baja)
            if (alertCode.includes('a008') || alertCode.includes('a004') || 
                alertCode.includes('a003') || alertCode.includes('a002') || alertCode.includes('a001') || 
                alertCode.includes('info')) {
                return 1;
            }
            
            // Por defecto (prioridad mínima)
            return 0;
        },
        
        getAlertVariant(codAlerta) {
            // Determinar el color del badge según el código de alerta
            if (!codAlerta) return 'secondary';
            
            const alertCode = codAlerta.toString().trim();
            
            // Códigos críticos - rojo
            if (alertCode.includes('A007') || alertCode.includes('A005') || alertCode.includes('alto') || alertCode.includes('error')) {
                return 'danger';
            }
            // Códigos de advertencia - amarillo
            if (alertCode.includes('A006') || alertCode.includes('warning') || alertCode.includes('alerta')) {
                return 'warning';
            }
            // Códigos informativos - azul
            if (alertCode.includes('A008') || alertCode.includes('A004') || 
                alertCode.includes('A003') || alertCode.includes('A002') || alertCode.includes('A001') || 
                alertCode.includes('info')) {
                return 'info';
            }
            
            // Por defecto - gris
            return 'secondary';
        },
        
        // ✅ Método para obtener el detalle específico de una alerta
        getDetalleAlertaEspecifico(unidad, codigoAlerta) {
            if (!codigoAlerta) return '';
            
            const codigo = codigoAlerta.trim();
            
            // Si existe el mapeo específico de alertas, usarlo
            if (unidad.alertas_detalle_map && unidad.alertas_detalle_map[codigo]) {
                return unidad.alertas_detalle_map[codigo];
            }
            
            // Fallback: buscar en el detalle completo
            if (unidad.detalle_alerta) {
                // Si solo hay un detalle, devolverlo completo
                if (!unidad.detalle_alerta.includes('|')) {
                    return unidad.detalle_alerta;
                }
                
                // Si hay múltiples detalles separados por |, intentar encontrar el específico
                const detalles = unidad.detalle_alerta.split(' | ');
                
                // Buscar el detalle que contenga el código específico
                const detalleEspecifico = detalles.find(detalle => 
                    detalle.toLowerCase().includes(codigo.toLowerCase()) ||
                    detalle.toLowerCase().includes(codigo.replace('A', 'a'))
                );
                
                // Si encontramos un detalle específico, devolverlo; si no, devolver el primero
                return detalleEspecifico || detalles[0] || unidad.detalle_alerta;
            }
            
            // Último fallback: descripción genérica basada en el código
            return this.getDescripcionGenericaAlerta(codigo);
        },
        
        // ✅ Método para obtener descripción genérica de alerta cuando no hay detalle específico
        getDescripcionGenericaAlerta(codigoAlerta) {
            const codigo = codigoAlerta.toString().trim().toUpperCase();
            
            const descripciones = {
                'A001': 'Rango de temperatura inusual - El equipo detectó temperaturas fuera del rango normal de operación',
                'A002': 'Capacidad de enfriamiento deficiente - El sistema no está enfriando de manera eficiente',
                'A003': 'El voltaje de la unidad exterior es 0 - Sin alimentación eléctrica en la unidad externa',
                'A004': 'Voltaje de operación de unidad interna del aire se encuentra fuera del rango normal - Problema de alimentación interna',
                'A005': 'El voltaje de operación de unidad externa del aire se encuentra fuera de rango normal - Problema de alimentación externa',
                'A006': 'Advertencia del sistema - El equipo requiere atención preventiva',
                'A007': 'Error crítico del equipo - Falla grave que requiere intervención inmediata',
                'A008': 'Información del sistema - Notificación de estado o configuración'
            };
            
            return descripciones[codigo] || `Código de alerta ${codigo} - Consulte el manual técnico para más detalles`;
        },
        
        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
    },
};
</script>

<style scoped>
/* Container principal */
.cbi-container {
    padding: 2rem;
    min-height: 100vh;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

/* Header Section */
.page-header {
    background: white;
    border-radius: 20px;
    padding: 2rem;
    margin-bottom: 2rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 1.5rem;
}

.header-icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    box-shadow: 0 8px 20px rgba(26, 39, 50, 0.3);
}

.page-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1a2732;
    margin: 0;
    text-shadow: 2px 2px 4px rgba(26, 39, 50, 0.3);
}

.page-subtitle {
    color: #718096;
    font-size: 1.1rem;
    margin: 0.5rem 0 0 0;
    font-weight: 400;
}

.btn-create {
    background: linear-gradient(135deg, #1a2732 0%, #3a4f5f 100%);
    border: none;
    color: white;
    padding: 0.75rem 2rem;
    border-radius: 15px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 6px 20px rgba(26, 39, 50, 0.3);
    display: flex;
    align-items: center;
}

.btn-create:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(26, 39, 50, 0.4);
    color: white;
}

/* Stats Cards */
.stats-cards {
    display: flex;
    gap: 1rem;
}

.stat-card {
    background: white;
    border-radius: 15px;
    padding: 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    border: 2px solid transparent;
    transition: all 0.3s ease;
    min-width: 180px;
}

.stat-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.stat-created {
    border-color: #c3e6cb;
    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
}

.stat-on {
    border-color: #c3e6cb;
    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
}

.stat-off {
    border-color: #f5c6cb;
    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
}

.stat-disconnected {
    border-color: #ffeaa7;
    background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
}

.stat-no-data {
    border-color: #bee5eb;
    background: linear-gradient(135deg, #d4f1f4 0%, #bee5eb 100%);
}

.stat-icon {
    width: 50px;
    height: 50px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    flex-shrink: 0;
}

.stat-on .stat-icon {
    background: #155724;
    color: white;
}

.stat-off .stat-icon {
    background: #721c24;
    color: white;
}

.stat-disconnected .stat-icon {
    background: #856404;
    color: white;
}

.stat-no-data .stat-icon {
    background: #0c5460;
    color: white;
}

.stat-content {
    display: flex;
    flex-direction: column;
}

.stat-number {
    font-size: 2rem;
    font-weight: 700;
    line-height: 1;
}

.stat-on .stat-number {
    color: #155724;
}

.stat-off .stat-number {
    color: #721c24;
}

.stat-disconnected .stat-number {
    color: #856404;
}

.stat-no-data .stat-number {
    color: #0c5460;
}

.stat-label {
    font-size: 0.9rem;
    font-weight: 600;
    margin-top: 0.25rem;
    color: #4a5568;
}

/* Filters Section */
.filters-card {
    background: white;
    border-radius: 15px;
    padding: 1.5rem;
    margin-bottom: 2rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.filters-content {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr auto;
    gap: 1rem;
    align-items: end;
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.filter-label {
    color: #2d3748;
    font-weight: 600;
    font-size: 0.9rem;
}

.filter-input,
.filter-select {
    padding: 0.75rem 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: white;
}

.filter-input:focus,
.filter-select:focus {
    outline: none;
    border-color: #1a2732;
    box-shadow: 0 0 0 3px rgba(26, 39, 50, 0.1);
}

.action-buttons-filter {
    display: flex;
    gap: 0.5rem;
    align-items: center;
}

.btn-search {
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    border: 2px solid #1a2732;
    color: white;
    padding: 0.75rem;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 45px;
    height: 45px;
}

.btn-search:hover:not(:disabled) {
    background: linear-gradient(135deg, #2a3f4f 0%, #3a4f5f 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(26, 39, 50, 0.3);
}

.btn-search:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.btn-clear {
    background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
    border: 2px solid #cbd5e0;
    color: #4a5568;
    padding: 0.75rem;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 45px;
    height: 45px;
}

.btn-clear:hover {
    background: linear-gradient(135deg, #edf2f7 0%, #e2e8f0 100%);
    border-color: #a0aec0;
    color: #2d3748;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(74, 85, 104, 0.2);
}

/* Botón filtro tipo */
.btn-filter-type {
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    border: 2px solid #1a2732;
    color: white;
    padding: 0.75rem 1rem;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 200px;
    justify-content: space-between;
}

.btn-filter-type:hover {
    background: linear-gradient(135deg, #2a3f4f 0%, #3a4f5f 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(26, 39, 50, 0.3);
    color: white;
}

/* Content Card */
.content-card {
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Loading */
.loading-container {
    padding: 4rem;
    text-align: center;
}

.loading-content {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
}

.loading-spinner {
    width: 3rem;
    height: 3rem;
}

.loading-text {
    color: #718096;
    font-size: 1.1rem;
    margin: 0;
}

/* Custom Table */
.table-container {
    padding: 2rem;
}

/* Banner informativo */
.info-banner {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    border: 1px solid #90caf9;
    border-radius: 12px;
    padding: 1rem 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 2px 8px rgba(21, 101, 192, 0.15);
}

.info-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.info-icon {
    color: #1565c0;
    font-size: 1.25rem;
    flex-shrink: 0;
}

.info-text {
    color: #0d47a1;
    font-size: 0.9rem;
    line-height: 1.4;
}

.info-text strong {
    font-weight: 700;
}

.custom-table {
    width: 100%;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.table-header {
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
}

.header-row {
    color: white;
    font-weight: 600;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.table-row {
    display: grid;
    grid-template-columns: 60px 3fr 200px 140px 140px;
    gap: 1.5rem;
    align-items: center;
    min-height: 70px;
}

.data-row {
    background: white;
    border-bottom: 1px solid #e2e8f0;
    transition: all 0.3s ease;
    margin-bottom: 2px;
    border-radius: 8px;
}

.data-row:hover {
    background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
    transform: translateX(5px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
    border-color: #cbd5e0;
}

.table-cell {
    padding: 1.2rem 0.75rem;
    display: flex;
    align-items: center;
    word-wrap: break-word;
    overflow-wrap: break-word;
    line-height: 1.4;
}

.cbi-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 0.5rem 0.75rem;
    border-radius: 12px;
    border: 1px solid #dee2e6;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.cbi-avatar {
    color: #1a2732;
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    color: white;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
}

.cbi-code {
    font-weight: 700;
    color: #2d3748;
    font-family: 'Courier New', monospace;
    font-size: 0.85rem;
    letter-spacing: 0.5px;
}

.sede-badge {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    color: #1565c0;
    padding: 0.5rem 1rem;
    border-radius: 25px;
    font-size: 0.8rem;
    font-weight: 700;
    border: 1px solid #90caf9;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(21, 101, 192, 0.15);
}

.status-badge {
    padding: 0.4rem 0.8rem;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.status-active {
    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
    color: #155724;
    border: 1px solid #c3e6cb;
}

.status-inactive {
    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
    color: #721c24;
    border: 1px solid #f5c6cb;
}

.status-on {
    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
    color: #155724;
    border: 1px solid #c3e6cb;
}

.status-off {
    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
    color: #721c24;
    border: 1px solid #f5c6cb;
}

.status-disconnected {
    background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
    color: #856404;
    border: 1px solid #ffeaa7;
}

.status-no-data {
    background: linear-gradient(135deg, #d4f1f4 0%, #bee5eb 100%);
    color: #0c5460;
    border: 1px solid #bee5eb;
}

.alert-badge {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.4rem 0.6rem;
    border-radius: 12px;
    cursor: help;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.alert-badge:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Contenedor para múltiples badges de alerta */
.alert-badges-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    align-items: center;
}

/* Wrapper individual para cada badge con tooltip */
.alert-badge-wrapper {
    display: inline-block;
}

/* Estilos para badges múltiples */
.alert-badge.multiple-alerts {
    font-size: 0.7rem;
    padding: 0.3rem 0.5rem;
    margin: 0;
    white-space: nowrap;
}

/* Tooltip personalizado para alertas */
.alert-tooltip {
    max-width: 350px;
    z-index: 1050;
}

.alert-tooltip .tooltip-inner {
    background-color: #1a2732;
    color: white;
    border-radius: 8px;
    padding: 0.75rem;
    font-size: 0.85rem;
    line-height: 1.4;
    text-align: left;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    word-wrap: break-word;
}

/* Mejorar la visibilidad de badges múltiples */
.alert-badge-wrapper:hover .alert-badge {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.action-buttons {
    display: flex;
    gap: 0.5rem;
}

.btn-edit,
.btn-view,
.btn-link {
    background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
    border: 1px solid #cbd5e0;
    color: #1a2732;
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

.btn-link {
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    color: white;
    border-color: #1a2732;
}

.btn-edit:hover,
.btn-view:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(26, 39, 50, 0.15);
    background: linear-gradient(135deg, #edf2f7 0%, #e2e8f0 100%);
    color: #1a2732;
}

.btn-link:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(26, 39, 50, 0.3);
    background: linear-gradient(135deg, #2a3f4f 0%, #3a4f5f 100%);
    color: white;
}

.btn-edit:disabled,
.btn-view:disabled,
.btn-link:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

/* Pagination */
.pagination-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 2.5rem 0;
    gap: 1.5rem;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 2rem;
    border-radius: 20px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.pagination-info {
    text-align: center;
    background: white;
    padding: 0.75rem 1.5rem;
    border-radius: 25px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(26, 39, 50, 0.1);
}

.pagination-info p {
    margin: 0;
    font-size: 0.95rem;
    color: #495057;
    font-weight: 600;
    text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
}

.custom-pagination {
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

/* Estilos personalizados para los botones de paginación */
.custom-pagination /deep/ .page-link {
    border: none;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    color: #1a2732;
    font-weight: 600;
    padding: 0.75rem 1rem;
    margin: 0 2px;
    border-radius: 10px;
    transition: all 0.3s ease;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.custom-pagination /deep/ .page-link:hover {
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(26, 39, 50, 0.3);
}

.custom-pagination /deep/ .page-item.active .page-link {
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    color: white;
    border-color: #1a2732;
    box-shadow: 0 4px 15px rgba(26, 39, 50, 0.4);
}

.custom-pagination /deep/ .page-item.disabled .page-link {
    background: #e9ecef;
    color: #6c757d;
    opacity: 0.6;
    cursor: not-allowed;
}

.custom-pagination /deep/ .page-item:first-child .page-link,
.custom-pagination /deep/ .page-item:last-child .page-link {
    border-radius: 10px;
}

/* Modal Styles */
.modal-form {
    padding: 1rem;
}

.cbi-form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

.form-label {
    color: #2d3748;
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
}

.form-input,
.form-select,
.form-textarea {
    padding: 0.75rem 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: white;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
    outline: none;
    border-color: #1a2732;
    box-shadow: 0 0 0 3px rgba(26, 39, 50, 0.1);
}

.form-textarea {
    resize: vertical;
    font-family: inherit;
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1rem;
}

.btn-cancel {
    padding: 0.75rem 1.5rem;
    border: 2px solid #cbd5e0;
    background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
    color: #4a5568;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-cancel:hover {
    background: linear-gradient(135deg, #edf2f7 0%, #e2e8f0 100%);
    border-color: #a0aec0;
    color: #2d3748;
}

.btn-save {
    padding: 0.75rem 1.5rem;
    border: none;
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    color: white;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-save:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(26, 39, 50, 0.3);
    color: white;
}

.btn-save:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
}

/* Details Modal */
.details-content {
    padding: 1rem;
}

.details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.detail-item.full-width {
    grid-column: 1 / -1;
}

.detail-label {
    color: #4a5568;
    font-weight: 600;
    font-size: 0.9rem;
}

.detail-value {
    color: #2d3748;
    font-size: 1rem;
}

.detail-description {
    color: #2d3748;
    background: #f7fafc;
    padding: 1rem;
    border-radius: 8px;
    border-left: 4px solid #1a2732;
    margin: 0.5rem 0 0 0;
}

.detail-dates {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e2e8f0;
}

/* Modal Styles */
.modal-options {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.option-card {
    background: white;
    border: 2px solid #e2e8f0;
    border-radius: 15px;
    padding: 1.0rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 1rem;
}

.option-card:hover {
    border-color: #1a2732;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(26, 39, 50, 0.15);
}

.option-card.active {
    border-color: #1a2732;
    background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
    box-shadow: 0 4px 15px rgba(26, 39, 50, 0.2);
}

.option-icon {
    width: 60px;
    height: 60px;
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    color: white;
    flex-shrink: 0;
}

.option-content {
    flex: 1;
}

.option-title {
    color: #1a2732;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
    font-size: 1.2rem;
}

.option-description {
    color: #718096;
    margin: 0 0 0.75rem 0;
    font-size: 0.95rem;
    line-height: 1.4;
}

.option-badge {
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    display: inline-block;
}

.unit-link {
    font-weight: 600;
    color: #1a2732;
    text-decoration: none;
    text-transform: capitalize;
    transition: color 0.3s ease;
}

.unit-link:hover {
    color: #dc3545;
    text-decoration: none;
}

/* Responsive */
@media (max-width: 1024px) {
    .filters-content {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
    
    .table-row {
        grid-template-columns: 50px 2fr 120px 100px 120px;
        gap: 0.5rem;
    }
}

@media (max-width: 768px) {
    .cbi-container {
        padding: 1rem;
    }
    
    .header-content {
        flex-direction: column;
        gap: 1rem;
        text-align: center;
    }
    
    .page-title {
        font-size: 2rem;
    }
    
    .table-row {
        display: block;
        padding: 1rem;
        border-radius: 10px;
        margin-bottom: 1rem;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }
    
    .table-header {
        display: none;
    }
    
    .table-cell {
        display: block;
        padding: 0.5rem 0;
        border-bottom: 1px solid #eee;
    }
    
    .table-cell:last-child {
        border-bottom: none;
    }
    
    .form-row {
        display: block;
    }
    
    .form-row .form-group {
        margin-bottom: 1rem;
    }
    
    .details-grid {
        grid-template-columns: 1fr;
    }
    
    .detail-dates {
        grid-template-columns: 1fr;
    }
    
    .action-buttons {
        flex-direction: column;
        gap: 0.5rem;
    }
}

/* Validation Loader Overlay */
.validation-loader-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(26, 39, 50, 0.95);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    backdrop-filter: blur(8px);
    animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.validation-loader-content {
    background: white;
    border-radius: 25px;
    padding: 3rem 4rem;
    text-align: center;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    max-width: 500px;
    animation: slideUp 0.4s ease-out;
}

@keyframes slideUp {
    from {
        transform: translateY(30px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.loader-spinner-container {
    margin-bottom: 2rem;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80px;
}

.loader-spinner {
    width: 4rem;
    height: 4rem;
    border-width: 4px;
}

.loader-text {
    color: #1a2732;
}

.loader-text h4 {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.75rem 0;
    color: #1a2732;
}

.loader-text p {
    font-size: 1.1rem;
    color: #718096;
    margin: 0 0 0.5rem 0;
    line-height: 1.5;
}

.loader-text small {
    font-size: 0.9rem;
    color: #a0aec0;
    display: block;
    margin-top: 0.5rem;
}
</style>